import React, { useState, useEffect, useCallback } from 'react';
import { useTier, controlHeight } from '../lib/tiers';
import { supabase } from '../lib/supabase';
import { useAuth } from '../components/AuthContext';

const DOMAIN_COLORS = {
  inventory:   '#1D9E75',
  tools:       '#378ADD',
  purchasing:  '#EF9F27',
  safety:      '#E24B4A',
  // WORK ORDERS IS GONE, NOT RESERVED -- Colby, Session 79.12: work orders
  // were CONCEDED TO D.A.R.C.C., so Stede does not need a placeholder for
  // them unless Joe and Angie direct otherwise. Measured first: 0 of 666
  // live notification rows. #307.
};

const DOMAINS = [
  {
    key: 'inventory',
    label: 'Inventory',
    description: 'Stock levels, receives, item changes',
    reserved: false,
    events: [
      { key: 'restock',             label: 'Restock activity',    description: 'A restock request is requested, received, or cancelled' },
      { key: 'restock_request',     label: 'Restock requested',   description: 'A team member submitted a restock request needing approval' },
      { key: 'off_catalog_request', label: 'Off-catalog request', description: 'A team member requested an item not in the catalog' },
      { key: 'audit',               label: 'Audit needed',        description: 'Stock went negative and needs a manual count' },
      // #371 and #425, Session 79.6, gate decision D12.
      //
      // UNDER INVENTORY, on Colby's ruling at 79.5. Note that the two
      // material_request_* toggles sit under Purchasing above while their colour
      // domain is inventory -- that mismatch is real, it is NOT this build's to
      // fix, and it must not be copied. A transfer moves stock between branches
      // and involves no vendor, no purchase order and no invoice at all.
      //
      // transfer_closed_short is deliberately absent and that is a decision
      // rather than an omission: stock was lost and the message carries its
      // value, so it is the audit ping and cannot be switched off. It follows
      // spare_request_created and restock_needed, both of which are likewise
      // absent from this screen.
      //
      // #647, Session 79.22b -- EIGHT, AND THREE ABSENT ON PURPOSE. This list was
      // complete at 79.6 and every transfer bell added since - made, arrived,
      // nothing more coming - arrived with no switch, on a screen that said
      // "9 of 9 events on". transferNotificationSwitches.test.js now fails the
      // next one. The three with no switch, each a decision: closed_short
      // (above); transfer_denied_successor, which carries an action somebody
      // must take (#559); and transfer_withdrawn, gate 371 D36 - it tells
      // somebody that work in front of them is gone.
      // #631, Session 79.22b -- and a FOURTH: transfer_handed_over, sent when
      // a handover is counted short or over. Stock is unaccounted for between
      // two named people, so it is the audit ping, like closed_short - gate
      // 553 B5: "No when short".
      { key: 'transfer_requested',  label: 'Transfer requested',  description: 'Another branch is asking your branch to send or make material' },
      { key: 'transfer_approved',   label: 'Transfer approved',   description: 'A manager approved a transfer you asked for, or one your branch must send' },
      { key: 'transfer_denied',     label: 'Transfer denied',     description: 'A manager denied your transfer request, with a reason' },
      { key: 'transfer_production_signed_off', label: 'Material made', description: 'The branch making material your branch asked for signed off a run, and it is ready to load' },
      // #682 / #702, Session 79.22b.6c.3 (B5) -- beside "Material made"; the words
      // are Colby's call C at the session's open (R1).
      { key: 'transfer_corrected', label: 'Transfer corrected', description: 'The other branch on a transfer corrected a number it recorded: what was made, what went on a truck, or what was counted in' },
      { key: 'transfer_shipped',    label: 'Transfer shipped',    description: 'Material has left the sending branch and is on its way to you' },
      { key: 'transfer_load_arrived', label: 'Load arrived',      description: 'A truckload of material you asked for, or approved, reached your branch and was counted in' },
      { key: 'transfer_received',   label: 'Transfer received',   description: 'The receiving branch booked in material your branch sent or made' },
      { key: 'transfer_closed_unfilled', label: 'Nothing more is coming', description: 'The sending branch will not send the rest of material your branch asked for' },
    ],
  },
  {
    key: 'tools',
    label: 'Tools',
    description: 'Tag-outs, returns, inspection alerts',
    reserved: false,
    events: [
      { key: 'tool_tagged_out',       label: 'Tool tagged out',       description: 'A tool is checked out by a team member' },
      { key: 'order_shipped',         label: 'Tool shipped',          description: 'A tool has been shipped to your branch' },
      { key: 'receipt_confirmed',     label: 'Receipt confirmed',     description: 'A shipped or repaired tool has been confirmed received' },
      { key: 'spare_request',         label: 'Spare requested',       description: 'A branch requested a spare tool' },
      { key: 'inspection_discrepancy', label: 'Inspection discrepancy', description: 'A logged inspection found one or more issues' },
    ],
  },
  {
    key: 'purchasing',
    label: 'Purchasing',
    description: 'Approvals, orders, receiving',
    reserved: false,
    events: [
      { key: 'request_approved', label: 'Spare request approved', description: 'A manager approved your spare tool request' },
      { key: 'request_rejected', label: 'Spare request denied', description: 'A manager denied your spare tool request' },
      { key: 'material_request_approved', label: 'Material request approved', description: 'A manager approved your material request' },
      { key: 'material_request_denied', label: 'Material request denied', description: 'A manager denied your material request' },
      // #592, gate 391 D17 -- switchable, beside the denial it reads like. The
      // two withdrawal bells are deliberately NOT here (D16): they tell somebody
      // that work in front of them is gone.
      { key: 'material_request_cancelled', label: 'Material request canceled', description: 'Purchasing canceled a request you asked for or approved' },
      // #614, gate 371 D40 -- switchable, beside the other request endings.
      { key: 'material_request_sent_as_transfer', label: 'Request sent as a transfer', description: 'Purchasing sent a request you asked for or approved to one of our own branches' },
      { key: 'purchase_approval_needed', label: 'Purchase approval needed', description: 'An order exceeds the spend threshold and needs your review' },
      { key: 'purchase_approval_pending', label: 'Purchase awaiting approval', description: 'Your request is on hold pending a threshold approval' },
      { key: 'purchase_approval_denied', label: 'Purchase order denied', description: 'A threshold-flagged order was denied and needs revision' },
      { key: 'purchase_approval_granted', label: 'Purchase approved', description: 'A threshold-flagged order was approved and is ready to place with the vendor' },
    ],
  },
  {
    key: 'safety',
    label: 'Safety',
    description: 'Inspection alerts and safety events',
    reserved: true,
    events: [
      { key: 'inspection_overdue', label: 'Inspection overdue', description: 'Safety inspection is past due' },
    ],
  },
  // THE WORK ORDERS ROW IS DELETED RATHER THAN LEFT "Module coming soon" --
  // Colby, Session 79.12: work orders were conceded to D.A.R.C.C. and Stede
  // needs no placeholder unless Joe and Angie direct otherwise. It promised a
  // module that is now somebody else's product, which is a worse promise than
  // an unbuilt one. SAFETY KEEPS ITS reserved:true ROW -- that module is still
  // Stede's and still coming. #307.
];

// #680, Session 79.22b.4 (with #672) -- THE SWITCH KEEPS ITS LOOK; THE TARGET
// MEETS THE FLOOR. The track is 20px tall and the button WAS the track, so with
// a mouse the only clickable thing was 20px against a floor of 24 -- on 87
// switches. The button now takes its height from tiers.js (24 with a mouse, 40
// with a finger) and the drawn track sits inside it, unchanged.
function Toggle({ enabled, onChange, disabled, size = 'md' }) {
  const { coarse } = useTier();
  const w = size === 'sm' ? 28 : 36;
  const h = size === 'sm' ? 16 : 20;
  const thumb = size === 'sm' ? 12 : 16;
  const travel = size === 'sm' ? 12 : 16;
  return (
    <button
      role="switch"
      aria-checked={enabled}
      onClick={() => !disabled && onChange(!enabled)}
      style={{
        minHeight: controlHeight(coarse, 'min'), minWidth: w,
        background: 'none', border: 'none', padding: 0,
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}
    >
      <span aria-hidden="true" style={{
        width: w, height: h, borderRadius: 999, boxSizing: 'border-box', padding: 2,
        background: disabled ? 'var(--border)' : enabled ? 'var(--fill-accent)' : 'var(--border-strong)',
        display: 'flex', alignItems: 'center',
        opacity: disabled ? 0.45 : 1, transition: 'background .15s',
      }}>
        <span style={{
          width: thumb, height: thumb, borderRadius: '50%', background: '#fff',
          transform: enabled && !disabled ? `translateX(${travel}px)` : 'translateX(0)',
          transition: 'transform .15s', display: 'block',
        }} />
      </span>
    </button>
  );
}

function ComingSoonBadge() {
  return (
    <span style={{
      fontSize: 10, color: 'var(--text-muted)',
      border: '0.5px solid var(--border)', borderRadius: 999,
      padding: '2px 7px', whiteSpace: 'nowrap', marginLeft: 6,
    }}>
      Soon
    </span>
  );
}

export default function NotificationPreferencesPage({ setActive }) {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [expandedDomains, setExpandedDomains] = useState({ inventory: true });
  const [mobileChannel, setMobileChannel] = useState('in_app');

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  useEffect(() => {
    if (!user) return;
    supabase
      .from('user_notification_preferences')
      .select('*')
      .eq('user_id', user.id)
      .then(({ data }) => {
        const map = {};
        (data || []).forEach(row => { map[row.event_type] = row.enabled; });
        setPrefs(map);
        setLoading(false);
      });
  }, [user]);

  const handleToggle = useCallback(async (eventType, domain, newVal) => {
    setPrefs(prev => ({ ...prev, [eventType]: newVal }));
    setSaving(prev => ({ ...prev, [eventType]: true }));
    await supabase
      .from('user_notification_preferences')
      .upsert(
        { user_id: user.id, event_type: eventType, enabled: newVal },
        { onConflict: 'user_id,event_type' }
      );
    setSaving(prev => ({ ...prev, [eventType]: false }));
  }, [user]);

  const handleDomainToggle = useCallback(async (domain, events, newVal) => {
    const updates = events.map(e => ({ user_id: user.id, event_type: e.key, enabled: newVal }));
    const newPrefs = { ...prefs };
    events.forEach(e => { newPrefs[e.key] = newVal; });
    setPrefs(newPrefs);
    await supabase
      .from('user_notification_preferences')
      .upsert(updates, { onConflict: 'user_id,event_type' });
  }, [user, prefs]);

  const isDomainEnabled = (events) => events.some(e => prefs[e.key] !== false);
  const enabledCount = (events) => events.filter(e => prefs[e.key] !== false).length;

  const toggleAccordion = (key) => {
    setExpandedDomains(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
        Loading preferences…
      </div>
    );
  }

  /* ── DESKTOP — full matrix ── */
  if (!isMobile) {
    return (
      <div style={{
        background: 'var(--surface-1)', borderRadius: 12,
        border: '0.5px solid var(--border)', overflow: 'hidden',
      }}>
        <div style={{ padding: '16px 20px', borderBottom: '0.5px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 2 }}>
            {setActive && (
              <button
                onClick={() => setActive('notifications')}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                  display: 'flex', alignItems: 'center', gap: 4,
                  fontSize: 13, color: 'var(--text-accent)', fontFamily: 'var(--font-sans)',
                }}
              >
                <i className="ti ti-arrow-left" style={{ fontSize: 14 }} aria-hidden="true" />
                Notifications
              </button>
            )}
          </div>
          <div style={{ fontSize: 16, fontWeight: 500, color: 'var(--text-primary)' }}>
            Notification preferences
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
            Changes save automatically. Controls apply to your account only.
          </div>
        </div>

        <div style={{ padding: '0 20px 20px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{
                  textAlign: 'left', fontSize: 11, fontWeight: 500,
                  color: 'var(--text-muted)', textTransform: 'uppercase',
                  letterSpacing: '0.07em', padding: '10px 12px',
                  borderBottom: '0.5px solid var(--border-strong)',
                  width: '46%',
                }}>Event</th>
                <th style={{
                  textAlign: 'center', fontSize: 11, fontWeight: 500,
                  color: 'var(--text-muted)', textTransform: 'uppercase',
                  letterSpacing: '0.07em', padding: '10px 12px',
                  borderBottom: '0.5px solid var(--border-strong)', width: 100,
                }}>In-app</th>
                <th style={{
                  textAlign: 'center', fontSize: 11, fontWeight: 500,
                  color: 'var(--text-muted)', textTransform: 'uppercase',
                  letterSpacing: '0.07em', padding: '10px 12px',
                  borderBottom: '0.5px solid var(--border-strong)', width: 120,
                }}>
                  Email <ComingSoonBadge />
                </th>
                <th style={{
                  textAlign: 'center', fontSize: 11, fontWeight: 500,
                  color: 'var(--text-muted)', textTransform: 'uppercase',
                  letterSpacing: '0.07em', padding: '10px 12px',
                  borderBottom: '0.5px solid var(--border-strong)', width: 120,
                }}>
                  Push <ComingSoonBadge />
                </th>
              </tr>
            </thead>
            <tbody>
              {DOMAINS.map(domain => (
                <React.Fragment key={domain.key}>
                  {/* Domain header row */}
                  <tr>
                    <td
                      colSpan={4}
                      style={{
                        padding: '12px 12px 8px',
                        background: 'var(--surface-0)',
                        opacity: domain.reserved ? 0.5 : 1,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{
                            width: 10, height: 10, borderRadius: '50%',
                            background: DOMAIN_COLORS[domain.key], flexShrink: 0,
                            display: 'inline-block',
                          }} />
                          <div>
                            <span style={{
                              fontSize: 13, fontWeight: 500,
                              color: domain.reserved ? 'var(--text-muted)' : 'var(--text-primary)',
                            }}>
                              {domain.label}
                              {domain.reserved && (
                                <span style={{
                                  fontSize: 11, fontWeight: 400, marginLeft: 8,
                                  border: '0.5px solid var(--border)', borderRadius: 999,
                                  padding: '2px 7px', color: 'var(--text-muted)',
                                }}>
                                  Module coming soon
                                </span>
                              )}
                            </span>
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>
                              {domain.description}
                            </div>
                          </div>
                        </div>
                        {!domain.reserved && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                              All {domain.label.toLowerCase()}
                            </span>
                            <Toggle
                              enabled={isDomainEnabled(domain.events)}
                              onChange={val => handleDomainToggle(domain.key, domain.events, val)}
                              disabled={false}
                            />
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Event rows */}
                  {domain.events.map((event, idx) => (
                    <tr key={event.key} style={{ opacity: domain.reserved ? 0.45 : 1 }}>
                      <td style={{
                        padding: '8px 12px 8px 28px',
                        borderBottom: idx === domain.events.length - 1
                          ? '0.5px solid var(--border-strong)'
                          : '0.5px solid var(--border)',
                      }}>
                        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{event.label}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 1 }}>{event.description}</div>
                      </td>
                      <td style={{
                        textAlign: 'center', verticalAlign: 'middle',
                        borderBottom: idx === domain.events.length - 1
                          ? '0.5px solid var(--border-strong)'
                          : '0.5px solid var(--border)',
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <Toggle
                            enabled={prefs[event.key] !== false}
                            onChange={val => handleToggle(event.key, domain.key, val)}
                            disabled={domain.reserved}
                            size="md"
                          />
                        </div>
                      </td>
                      <td style={{
                        textAlign: 'center', verticalAlign: 'middle',
                        borderBottom: idx === domain.events.length - 1
                          ? '0.5px solid var(--border-strong)'
                          : '0.5px solid var(--border)',
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <Toggle enabled={false} onChange={() => {}} disabled={true} size="md" />
                        </div>
                      </td>
                      <td style={{
                        textAlign: 'center', verticalAlign: 'middle',
                        borderBottom: idx === domain.events.length - 1
                          ? '0.5px solid var(--border-strong)'
                          : '0.5px solid var(--border)',
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <Toggle enabled={false} onChange={() => {}} disabled={true} size="md" />
                        </div>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  /* ── MOBILE — channel tabs + accordion ── */
  const channels = [
    { key: 'in_app', label: 'In-app' },
    { key: 'email',  label: 'Email', soon: true },
    { key: 'push',   label: 'Push',  soon: true },
  ];

  return (
    <div style={{
      background: 'var(--surface-1)', borderRadius: 12,
      border: '0.5px solid var(--border)', overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{ padding: '12px 16px', borderBottom: '0.5px solid var(--border)' }}>
        {setActive && (
          <button
            onClick={() => setActive('notifications')}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6,
              fontSize: 13, color: 'var(--text-accent)', fontFamily: 'var(--font-sans)',
            }}
          >
            <i className="ti ti-arrow-left" style={{ fontSize: 14 }} aria-hidden="true" />
            Notifications
          </button>
        )}
        <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--text-primary)' }}>
          Notification preferences
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
          Changes save automatically.
        </div>
      </div>

      {/* Channel tabs */}
      <div style={{ display: 'flex', borderBottom: '0.5px solid var(--border)' }}>
        {channels.map(ch => (
          <button
            key={ch.key}
            onClick={() => !ch.soon && setMobileChannel(ch.key)}
            style={{
              flex: 1, textAlign: 'center', padding: '10px 4px',
              fontSize: 13, cursor: ch.soon ? 'default' : 'pointer',
              color: mobileChannel === ch.key ? 'var(--text-accent)' : 'var(--text-muted)',
              fontWeight: mobileChannel === ch.key ? 500 : 400,
              background: 'none',
              // Found by `no-dupe-keys` at 79.14d, on the lint config's first
              // run. `borderBottom` was declared TWICE with `border: 'none'`
              // between them, so the first was dead — the later key wins in an
              // object literal. The outcome was right by luck and the code read
              // as if two things were happening.
              //
              // `border: 'none'` is gone rather than kept, because mixing the
              // shorthand with a `borderBottom` longhand on one element is the
              // defect 74t fixed in Overlay.js and QueueTabs.js carries a
              // comment about: React warns on every rerender and drops one of
              // them. The three longhands say the same thing and cannot fight.
              borderTop: 'none', borderLeft: 'none', borderRight: 'none',
              borderBottom: mobileChannel === ch.key
                ? '2px solid var(--fill-accent)'
                : '2px solid transparent',
              fontFamily: 'var(--font-sans)',
            }}
          >
            {ch.label}
            {ch.soon && (
              <span style={{ fontSize: 9, color: 'var(--text-muted)', marginLeft: 4 }}>(soon)</span>
            )}
          </button>
        ))}
      </div>

      {/* Accordion sections */}
      {DOMAINS.map(domain => (
        <div key={domain.key} style={{ borderBottom: '0.5px solid var(--border)', opacity: domain.reserved ? 0.45 : 1 }}>
          {/* Accordion header */}
          <div
            onClick={() => !domain.reserved && toggleAccordion(domain.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '12px 16px',
              cursor: domain.reserved ? 'not-allowed' : 'pointer',
            }}
          >
            <span style={{
              width: 10, height: 10, borderRadius: '50%',
              background: DOMAIN_COLORS[domain.key], flexShrink: 0,
              display: 'inline-block',
            }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: domain.reserved ? 'var(--text-muted)' : 'var(--text-primary)' }}>
                {domain.label}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                {domain.reserved
                  ? 'Module coming soon'
                  : mobileChannel === 'in_app'
                    ? `${enabledCount(domain.events)} of ${domain.events.length} events on`
                    : 'Coming soon'
                }
              </div>
            </div>
            <Toggle
              enabled={!domain.reserved && isDomainEnabled(domain.events)}
              onChange={val => handleDomainToggle(domain.key, domain.events, val)}
              disabled={domain.reserved || mobileChannel !== 'in_app'}
              size="sm"
            />
            {!domain.reserved && (
              <i
                className={`ti ti-chevron-${expandedDomains[domain.key] ? 'up' : 'down'}`}
                style={{ fontSize: 14, color: 'var(--text-muted)' }}
                aria-hidden="true"
              />
            )}
          </div>

          {/* Accordion body */}
          {!domain.reserved && expandedDomains[domain.key] && (
            <div style={{ background: 'var(--surface-0)', paddingBottom: 4 }}>
              {domain.events.map(event => (
                <div
                  key={event.key}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 16px 10px 36px',
                  }}
                >
                  <div>
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{event.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{event.description}</div>
                  </div>
                  <Toggle
                    enabled={prefs[event.key] !== false}
                    onChange={val => handleToggle(event.key, domain.key, val)}
                    disabled={mobileChannel !== 'in_app'}
                    size="sm"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

